import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Icon, { IconType } from '@components/atoms/icon';
import { triggerToast } from '@components/atoms/toast';
import { APP_VERSIONS } from '@hooks/useIsAppView';

interface TabRoute {
  key: string;
  name: string;
  label: string;
  icon: IconType;
  href: string;
  isDeepLink?: boolean;
}

interface BottomTabNavProps {
  isAppView: boolean;
  appVersion: string;
  onTabChange?: (routeName: string, href: string) => void;
  className?: string;
}

const BottomTabNav: React.FC<BottomTabNavProps> = ({
  isAppView,
  appVersion,
  onTabChange,
  className = ''
}) => {
  const router = useRouter();
  const [fontSize, setFontSize] = useState(12);

  const routes: TabRoute[] = [
    {
      key: 'myteam',
      name: 'MyTeam',
      label: 'My Team',
      icon: 'Trophy',
      href: '/my-team/pick'
    },
    {
      key: 'content',
      name: 'Content',
      label: 'Content',
      icon: 'News',
      href: '/category/latest'
    },
    {
      key: 'toolbox',
      name: 'Toolbox',
      label: 'Toolbox',
      icon: 'Toolbox',
      href: '/tools'
    },
    {
      key: 'settings',
      name: 'Settings',
      label: 'Settings',
      icon: 'Settings',
      href: 'fantasyfootballhub://settings',
      isDeepLink: true
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      const textSize = window.innerWidth < 480 ? 12 : 14;
      setFontSize(textSize);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabPress = (
    routeName: string,
    href: string,
    isDeepLink?: boolean
  ) => {
    onTabChange?.(routeName, href);

    if (isDeepLink) {
      // Handle deep link navigation
      try {
        window.location.href = href;
      } catch (error) {
        triggerToast({
          title: 'Deep link navigation failed',
          variant: 'error'
        });
      }
    } else {
      router.push(href);
    }
  };

  const isActiveRoute = (href: string, isDeepLink?: boolean) => {
    if (isDeepLink) {
      return false;
    }
    return router.asPath === href;
  };

  // Don't render if not in app view
  if (!isAppView || appVersion === APP_VERSIONS.WEBVIEW) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 ${className}`}>
      <div className="bg-neutral-800 rounded-t-[50px] px-4 pb-safe-bottom">
        <div className="flex flex-row">
          {routes.map((route) => {
            const isActive = isActiveRoute(route.href, route.isDeepLink);

            return (
              <button
                key={`tab-${route.key}`}
                onClick={() =>
                  handleTabPress(route.name, route.href, route.isDeepLink)
                }
                className="flex-1 flex flex-col items-center pt-2 pb-1 transition-colors duration-200 hover:text-blue-400 focus:outline-none focus:text-blue-400"
                aria-label={`Navigate to ${route.label}`}
                role="tab"
                aria-selected={isActive}
              >
                <Icon
                  name={route.icon}
                  style={{
                    height: 25,
                    width: 25
                  }}
                  className={isActive ? 'text-primary-600' : 'text-white'}
                />
                <div className="pb-1 leading-none">
                  <span
                    className={`transition-colors duration-200 ${
                      isActive ? 'text-primary-600' : 'text-white'
                    }`}
                    style={{ fontSize: `${fontSize}px`, lineHeight: 1 }}
                  >
                    {route.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BottomTabNav;
